import {VueController} from '@/service/support/vuex-controller';
import vaultService from '@/service/vault-service';
import * as awsVars from '@/config/aws-exports';
let awsmobile = awsVars.default(process.env);
import moment from "moment";
import toMaterialStyle from 'material-color-hash';
import orgService from "@/service/org-service";
import {EventBus} from "@/service/support/event-bus";
import Auth from "@aws-amplify/auth";
import WorkflowStatesProgress from '@/components/WorkflowStatesProgress.vue';

export default VueController("Vaults", {
    components: {
        WorkflowStatesProgress
    },
    data() {
        return {
            newVaultName: "",
            viewMode: 1,
            vaultMax: '',
            disableAddButton: false,
            snackBar: false,
            snackBarNotify: "",
            snackBarType: "",
            logoBucket: null,
	        searchVault: '',
			privateMode: false,
			showArchivedPortals: false,
			vaultsTablePage: 1,
			vaultsTablePageCount: 0,
			vaultsTableItemsPerPage: 50,
			selectedWorkflowStates: [], // Array of selected workflow state IDs
			// Column-specific filters
			filters: {
				name: '',
				tags: '',
				num_tasks_open: [0, 100],
				earliest_open_task_date: '',
				_lastChangedAt: '',
				workflowStateName: ''
			}
        };
    },
    state: [
        "vaults",
        "cognito",
	    "modals",
        "organization",
	    "organizationId",
        "user",
	    "pageIsLoading",
	    "embeddedFullScreen"
    ],
    computed: {
    	hasExternalVault() {
    		return !this.organization || this.vaults.find(vault => vault._deleted !== 1 && vault.org_id !== this.organization.id);
	    },
	    
	    // Method to check if a workflow state ID exists in current organization states
	    isWorkflowStateOrphaned() {
	    	return (vault) => {
	    		if (!vault.workflowStateId || !this.organization || !this.organization.workflowStates) {
	    			return false;
	    		}
	    		
	    		// Check if the vault's workflowStateId exists in the organization's current workflow states
	    		const stateExists = this.organization.workflowStates.find(state => state.id === vault.workflowStateId);
	    		return !stateExists;
	    	}
	    },
	    
	    // Method to get the display name for workflow state, handling orphaned states
	    getWorkflowStateDisplayName() {
	    	return (vault) => {
	    		if (!vault.workflowStateId && !vault.workflowStateName) {
	    			return null;
	    		}
	    		
	    		// If workflowStateName exists, check if it's orphaned
	    		if (vault.workflowStateName) {
	    			if (this.isWorkflowStateOrphaned(vault)) {
	    				return vault.workflowStateName + ' (Deleted)';
	    			}
	    			return vault.workflowStateName;
	    		}
	    		
	    		// If workflowStateId exists but no name, look it up from organization states
	    		if (vault.workflowStateId && this.organization && this.organization.workflowStates) {
	    			const state = this.organization.workflowStates.find(s => s.id === vault.workflowStateId);
	    			if (state) {
	    				return state.name;
	    			}
	    			// If ID exists but state not found in organization, it's orphaned
	    			return 'Unknown State (Deleted)';
	    		}
	    		
	    		// Fallback to "Not Set" if no name is available
	    		return null;
	    	}
	    },
	    // Cache the user's favorite vault ids as a Set for O(1) lookups in
	    // sort comparators and templates that iterate every vault.
	    favoriteVaultSet() {
	    	return new Set((this.user && this.user.favoriteVaults) || []);
	    },
	    workflowStateOptions() {
	    	let options = [];
	    	
	    	// Add "Not Set" option
	    	options.push({ id: null, name: 'Not Set' });
	    	
	    	// Add workflow states from organization
	    	if (this.organization && this.organization.workflowStates) {
	    		this.organization.workflowStates.forEach(state => {
	    			options.push({ id: state.id, name: state.name });
	    		});
	    	}
	    	
	    	return options;
	    },
		userCanCreateVaults() {
			return this.cognito &&
				(this.cognito.attributes["custom:mutableUserType"] === "admin" ||
					(this.cognito.attributes["custom:mutableUserType"] === "member" &&
						this.organization &&
						!!this.organization.permissions.find(perm => perm['create_vault'] === true && perm.user_id === this.user.id && perm._deleted !== 1)
					)
				);
		},
		// Check if user has any archived portals to show the toggle
		hasArchivedPortals() {
			if (!this.vaults || !this.cognito) return false;
			let userId = this.cognito.attributes.sub;
			let userType = this.cognito.attributes["custom:mutableUserType"];
			
			// Only show for members/admins who can create vaults
			if (userType === "guest") return false;
			
			return this.vaults.some(vault => 
				vault._deleted !== 1 && 
				vault.archived === true &&
				((this.user && userType !== "guest" && vault.org_id === this.user.memberOf) ||
				 (vault.permissions && vault.permissions.some(perm => 
					perm._deleted !== 1 && perm.user_id === userId
				 )))
			);
		},
	    responsiveVaultHeaderLayout() {
			let headers = [{text: "Client Portal", value: "name", sortable: true, width: "60%", align: "start"}];
			
			// Show tasks column if organization tier supports it
			if (this.organization && (this.organization.tier === 'enterprise' || this.organization.tier === 'large firm')) {
				headers.push({text: "Tasks", value: "num_tasks_open", sortable: true, width: "180px", align: "start"});
			}

			if (this.$vuetify.breakpoint.name === "sm") {
				headers.push({text: "Members", value: "numberOfMembers", sortable: false});
				// Add workflow state column for admins/members who have workflow states enabled (before tags)
				if (this.userCanCreateVaults && this.organization && this.organization.workflowStatesEnabled && (this.organization.tier === 'enterprise' || this.organization.tier === 'large firm')) {
					headers.push({text: "Workflow State", value: "workflowStateName", sortable: true, width: "140px", align: "start"});
				}
				// Add tags last (only for users who can create vaults)
				if (this.userCanCreateVaults) {
					headers.push({text: "Tags", value: "tags", sortable: false, width: "180px", align: "start"});
				}
			} else if (this.$vuetify.breakpoint.name !== "xs") {
				if (this.organization && (this.organization.tier === 'enterprise' || this.organization.tier === 'large firm')) {
					headers.push({text: "Earliest Due Date", value: "earliest_open_task_date", sortable: true, align: 'start', width: "140px"});
				}

				headers.push({text: "Last Modified Date", value: "_lastChangedAt", sortable: true, width: "150px"});
				headers.push({text: "Guests", value: "numberOfMembers", sortable: false, width: "140px"});
				// Add workflow state column for admins/members who have workflow states enabled (between guests and tags)
				if (this.userCanCreateVaults && this.organization && this.organization.workflowStatesEnabled && (this.organization.tier === 'enterprise' || this.organization.tier === 'large firm')) {
					headers.push({text: "Workflow State", value: "workflowStateName", sortable: true, width: "140px", align: "start"});
				}
				// Add tags second to last (only for users who can create vaults)
				if (this.userCanCreateVaults) {
					headers.push({text: "Tags", value: "tags", sortable: false, width: "180px", align: "start"});
				}
				headers.push({text: "Actions", value: "actions", sortable: false, width: "120px"});
			}
			return headers;
	    },
        vaultCeiling() {
            if (this.organization) {
                if (this.organization.tier.toLowerCase() !== 'enterprise') {
                    return this.vaults.length >= this.vaultMax-20;
                } else {
                    return false;
                }
            } else {
                return false;
            }
        },
        filterDeletedVaults() {
            if (this.vaults) {
                return this.vaults.filter(vault => vault._deleted !== 1);
            }
        },
	    vaultsNotDeletedAndAccepted() {
    		//console.time('vault filter');
        	let nvdaa = [];
        	let self = this;

		    if (self.vaults && self.cognito) {
		    	let userId = self.cognito.attributes.sub;
		    	let userType = self.cognito.attributes["custom:mutableUserType"];

				let memberCanAccessVaults = false;
				if (self.organization) {
					let memberOrgPermissions =
						self.organization.permissions.find(perm =>
							perm._deleted !== 1 && perm.invitation_accepted === "true" && perm.user_id === userId
						);
					if (memberOrgPermissions && memberOrgPermissions.access_vault === true) {
						memberCanAccessVaults = true;
					}
				}

			    nvdaa = self.vaults.filter(vault =>
				    vault._deleted !== 1
				    && ((self.user && userType !== "guest" && vault.org_id === self.user.memberOf && (userType === "admin" || memberCanAccessVaults)) ||
						vault.permissions && vault.permissions.some(perm =>
				        	perm._deleted !== 1 && perm.user_id === userId
						))
				    && (self.showArchivedPortals || !vault.archived)
			    );
		    }

		    const favoriteSet = self.favoriteVaultSet;
		    nvdaa = nvdaa.sort((a, b) => {
		    	const aFav = favoriteSet.has(a.id);
		    	const bFav = favoriteSet.has(b.id);
			    if (aFav && !bFav) return -1;
			    if (!aFav && bFav) return 1;
			    return a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
		    });
			//console.timeEnd('vault filter');
		    return nvdaa;
	    },
	    vaultsNotDeletedAndPendingInvitation() {
		    if (this.vaults && this.cognito) {
			    let userId = this.cognito.attributes.sub;
			    let userType = this.cognito.attributes["custom:mutableUserType"];

			    return this.vaults.filter(vault =>
				    vault._deleted !== 1
				    && ((userType === "guest") || (this.user && vault.org_id !== this.user.memberOf))
				    && vault.permissions && vault.permissions.some(perm =>
					    perm.user_id === userId && perm.invitation_accepted.match(/none/) && perm._deleted !== 1
				    ));
		    } else {
			    return [];
		    }
	    },
		// Computed property to ensure reactivity when any filter changes
		filteredVaults() {
			return this.customVaultFilter(this.vaultsNotDeletedAndAccepted, this.searchVault, null, null);
		}
    },
	watch: {
		// Reset to page 1 when search changes
		searchVault() {
			this.vaultsTablePage = 1;
		},
		// Reset to page 1 when any filter changes
		filters: {
			handler() {
				this.vaultsTablePage = 1;
			},
			deep: true
		},
		// Reset to page 1 when items per page changes
		vaultsTableItemsPerPage() {
			this.vaultsTablePage = 1;
		}
	},
	created() {
		this.logoBucket = awsmobile.aws_org_logos_s3_bucket;
	},
    mounted() {
		this.pageIsLoading = true;
        let self = this;

		let _privateMode = !!self.getLocalStoreValue("privateMode");
		self.privateMode = _privateMode;

        if (self.$route.query.embedded === "true" || self.$route.query.embedded === true) {
		    self.embeddedFullScreen = true;
	    }

        if (self.cognito && (self.cognito.attributes['custom:mutableUserType'] !== "admin" && self.cognito.attributes['custom:mutableUserType'] !== "member")) {
            this.viewMode = 1;
        } else {
	        this.viewMode = 0;
        }
        self.logoBucket = awsmobile.aws_org_logos_s3_bucket;
        self.vaults = [
            {
                name: "placeholder",
                id: 0,
                org_id: 0,
                placeholder: true
            }
        ];
		//console.time('getVaults');

        // F4: paint the first page as soon as it arrives instead of waiting
        // for fetchAllVaultPages to walk every page. For single-page orgs
        // (the common case) this is functionally identical; for huge orgs
        // it converts a long blank screen into an immediately useful list
        // that grows as later pages stream in.
        let firstPaintDone = false;
        const decorateVaults = (vaults) => {
            for (let i = 0; i < vaults.length; i++) {
                if (!Array.isArray(vaults[i].tags)) vaults[i].tags = [];
                vaults[i]._editTagsArray = [...vaults[i].tags];
            }
            return vaults;
        };

        self.fetchAllVaultPages(undefined, (page, soFar) => {
                // soFar is the same array fetchAllVaultPages mutates; assign
                // a fresh slice so Vue picks up the change reliably.
                self.vaults = decorateVaults(soFar.slice());
                if (!firstPaintDone) {
                    self.pageIsLoading = false;
                    firstPaintDone = true;
                }
            })
            .then(async response => {
				// If pagination produced zero pages (no callback fired), make
				// sure we clear the placeholder and stop the spinner.
				if (!firstPaintDone) {
					self.vaults = [];
					self.pageIsLoading = false;
				}

				//console.timeEnd('getVaults');
            	let tmpOrgId = null;
				let updateVaults = false;
            	for (let r = 0; r < response.length; r++) {

            		if (!tmpOrgId && response[r].org_id) {
			            tmpOrgId = response[r].org_id;
		            } else if (response[r].org_id !== tmpOrgId) {
            			tmpOrgId = null;
            			break;
		            }
	            }
				if(self.organization){
					for(let i=0;i<response.length; i++){
						//Double check to make sure org is on the vault level
						if(self.organization && self.organization.id && response[i].org_id === self.organization.id && response[i].orgTier !== self.organization.tier){
							updateVaults = true;
						}
					}
				}
				if(updateVaults){
					vaultService.mutation("updateVaultForTier",{}).then(response =>{
						for (let i = 0; i < self.vaults.length; i++) {
							if (self.vaults[i].org_id === self.organization.id) {
								self.vaults[i].orgTier = self.organization.tier;
							}
						}
					}).catch(e =>{
						vaultService._log("Updating vaults to new tier failed : " + JSON.stringify(e));
					});
				}
            	if (tmpOrgId) {
		            self.organizationId = tmpOrgId;
		            EventBus.$emit("refreshlazy", {oldId: "", newId: self.organizationId});
	            }

            	//DONE: If 1 vault, set org id
	            if (self.$route.query.accept && response && response.length === 1 && response[0].id === self.$route.query.accept) {
		            self.acceptInvite(response[0], true);
	            } else if (response && response.length === 1 && !response[0]._deleted && !self.isVaultPendingInvitation(response[0]) && self.cognito && self.cognito.attributes["custom:mutableUserType"] === "guest") {
					console.log(`Pushing to portal ${response[0].id} is portal deleted ? ${response[0]._deleted}`);
					self.$router.push({name: 'Portal', params: {vaultid: response[0].id, orgid: response[0].org_id, folderid: 'main'}});
	            } else {
		            if (self.organization) {
		            	self.organizationId = self.organization.logo_s3_path ? self.organization.logo_s3_path : "none";
						self.organizationId = self.organizationId.replace(".png", "");
			            switch (self.organization.tier.toLowerCase()) {
				            case 'starter':
					            self.vaultMax = 50;
					            break;
				            case 'professional':
					            self.vaultMax = 1000;
					            break;
				            case 'large firm':
					            self.vaultMax = 10000;
					            break;
				            case 'enterprise' :
					            self.vaultMax = 'unlimited'
					            break;
				            default:
					            self.vaultMax = 0;
			            }
		            } else {
		            	self.organizationId = "none";
		            }

					// F4: incremental rendering already populated self.vaults
					// page-by-page via the onPage callback above. Re-assigning
					// the final response here ensures any post-load mutations
					// (e.g. updateVaultForTier) see the canonical list, while
					// decorateVaults stays idempotent.
					self.vaults = decorateVaults(response.slice());
					self.pageIsLoading = false;

	            }
				//console.timeEnd('mounted');
            }).catch(error => {
            	console.error(error);
	            self.pageIsLoading = false;
            });

    },
    methods: {
		// Walks the paginated getVaultsByOrgIdPaginated query until nextToken is
		// null and returns the accumulated vault list. Used instead of the
		// single-shot getVaultsByOrgId query to stay under AppSync's 1MB
		// response limit on large orgs.
		//
		// F4: optional onPage callback fires after each page so callers can
		// incrementally render. Signature: onPage(items, accumulatedSoFar).
		// Errors thrown from onPage are logged but do not abort pagination.
		// The callback is also called with an empty items[] only if the very
		// first page returns nothing; downstream code can rely on at least
		// one invocation when there is at least one page.
		async fetchAllVaultPages(templatesOnly, onPage, accumulated, nextToken) {
			accumulated = accumulated || [];
			let params = { limit: 2500 };
			if (templatesOnly === true) {
				params.templatesOnly = true;
			}
			if (nextToken) {
				params.nextToken = nextToken;
			}

			let result = await vaultService.query("getVaultsByOrgIdPaginated", params, undefined, {vuexCache: false});
			let items = result && result.items ? result.items : [];
			let responseNextToken = result && result.nextToken ? result.nextToken : null;

			accumulated = accumulated.concat(items);

			if (typeof onPage === "function") {
				try {
					onPage(items, accumulated);
				} catch (cbErr) {
					console.error("fetchAllVaultPages onPage callback error:", cbErr);
				}
			}

			if (responseNextToken) {
				return this.fetchAllVaultPages(templatesOnly, onPage, accumulated, responseNextToken);
			}

			return accumulated;
		},
		customVaultFilter(items, search, filter, headers) {
			// If no filters are active, return all items
			const hasActiveFilters = Object.values(this.filters).some(filterValue => {
				if (Array.isArray(filterValue)) {
					// For array filters (like range slider), check if it's different from default
					return filterValue.length === 2 && (filterValue[0] > 0 || filterValue[1] < 100);
				} else {
					// For string filters, check if trimmed value exists
					return filterValue && filterValue.trim();
				}
			});
			if (!hasActiveFilters && (!search || !search.trim())) {
				return items;
			}

			return items.filter(item => {
				// Handle global search if provided
				if (search && search.trim()) {
					const searchLower = search.toLowerCase();
					const matchesGlobalSearch = 
						(item.name && item.name.toLowerCase().includes(searchLower)) ||
						(item.org_name && item.org_name.toLowerCase().includes(searchLower)) ||
						(item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchLower)));
					if (!matchesGlobalSearch) return false;
				}

				// Handle column-specific filters
				// Name filter
				if (this.filters.name && this.filters.name.trim()) {
					const nameFilter = this.filters.name.toLowerCase();
					if (!item.name || !item.name.toLowerCase().includes(nameFilter)) {
						return false;
					}
				}

				// Tags filter
				if (this.filters.tags && this.filters.tags.trim()) {
					const tagFilter = this.filters.tags.toLowerCase();
					if (!item.tags || !item.tags.some(tag => tag.toLowerCase().includes(tagFilter))) {
						return false;
					}
				}

				// Task completion filter
				if (this.filters.num_tasks_open && Array.isArray(this.filters.num_tasks_open)) {
					const [minPercent, maxPercent] = this.filters.num_tasks_open;
					const tasksOpen = parseInt(item.num_tasks_open) || 0;
					const tasksClosed = parseInt(item.num_tasks_closed) || 0;
					const totalTasks = tasksOpen + tasksClosed;
					const completionPercent = totalTasks > 0 ? Math.round((tasksClosed / totalTasks) * 100) : 0;
					
					// Filter out items where completion percentage is outside the selected range
					if (completionPercent < minPercent || completionPercent > maxPercent) {
						return false;
					}
				}

				// Earliest due date filter
				if (this.filters.earliest_open_task_date && this.filters.earliest_open_task_date.trim()) {
					const dateFilter = this.filters.earliest_open_task_date.toLowerCase();
					if (item.earliest_open_task_date && item.earliest_open_task_date !== -1 && item.num_tasks_open > 0) {
						const formattedDate = this.yyyymmddTOmmddyyyy(item.earliest_open_task_date, true);
						if (!formattedDate || !formattedDate.toLowerCase().includes(dateFilter)) {
							return false;
						}
					} else if (dateFilter.includes('none') || dateFilter.includes('no date')) {
						// Allow filtering for items with no due date
					} else {
						return false;
					}
				}

				// Last modified date filter
				if (this.filters._lastChangedAt && this.filters._lastChangedAt.trim()) {
					const dateFilter = this.filters._lastChangedAt.toLowerCase();
					if (item.id !== 0 && this.user) {
						const latestTimestamp = item.last_upload_at > item.last_download_at ? item.last_upload_at : item.last_download_at;
						if (latestTimestamp) {
							const formattedDate = this.momentDate(latestTimestamp, true);
							if (!formattedDate || !formattedDate.toLowerCase().includes(dateFilter)) {
								return false;
							}
						} else if (!dateFilter.includes('none') && !dateFilter.includes('no date')) {
							return false;
						}
					} else if (!dateFilter.includes('none') && !dateFilter.includes('no date')) {
						return false;
					}
				}

				// Workflow state filter (supports multiple states separated by |)
				if (this.filters.workflowStateName && this.filters.workflowStateName.trim()) {
					const workflowFilters = this.filters.workflowStateName.toLowerCase().split('|');
					let matches = false;
					
					for (let filter of workflowFilters) {
						filter = filter.trim();
						if (filter === 'not set') {
							// Handle "not set" case - no workflow state or orphaned state
							if (!item.workflowStateName || this.isWorkflowStateOrphaned(item)) {
								matches = true;
								break;
							}
						} else if (item.workflowStateName && item.workflowStateName.toLowerCase().includes(filter)) {
							// Handle exact state name match
							matches = true;
							break;
						}
					}
					
					if (!matches) {
						return false;
					}
				}

				return true;
			});
		},
	    portalTableSort(items, index, isDesc) {
	    	let self = this;
	    	const favoriteSet = self.favoriteVaultSet;

		    items.sort((a, b) => {
		    	let sortResult = 0;
		    	if (!index || Array.isArray(index) && index.length === 0) {
				    sortResult = a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
			    } else {
				    for (let i = 0; i < index.length; i++) {
					    let subIndex = index[i];
					    let subIsDesc = isDesc[i];

					    //console.log(`Sort index ${subIndex} descending ${JSON.stringify(subIsDesc)} is ${typeof subIsDesc}`);

					    const aFav = favoriteSet.has(a.id);
					    const bFav = favoriteSet.has(b.id);
					    if (aFav && !bFav) {
						    sortResult = -1;
					    } else if (!aFav && bFav) {
						    sortResult = 1;
					    } else if (subIndex === "name") {
						    if (!subIsDesc) {
							    sortResult = a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
						    } else {
							    sortResult = b.name.localeCompare(a.name, 'en', { sensitivity: 'base' });
						    }
					    } else if (subIndex === "_lastChangedAt") {
						    let latestA = a.last_upload_at > a.last_download_at ? a.last_upload_at : a.last_download_at;
						    let latestB = b.last_upload_at > b.last_download_at ? b.last_upload_at : b.last_download_at;

						    if (!subIsDesc) {
							    return latestA < latestB ? -1 : 1;
						    } else {
							    return latestB < latestA ? -1 : 1;
						    }
					    } else if (subIndex === "num_tasks_open" || subIndex === "earliest_open_task_date") {
							// Calculate completion percentages
							let aTotalTasks = a.num_tasks_open + a.num_tasks_closed;
							let bTotalTasks = b.num_tasks_open + b.num_tasks_closed;
							let aCompletionPercent = aTotalTasks > 0 ? (a.num_tasks_closed / aTotalTasks) * 100 : 0;
							let bCompletionPercent = bTotalTasks > 0 ? (b.num_tasks_closed / bTotalTasks) * 100 : 0;

							// First sort by completion percentage
							if (aCompletionPercent !== bCompletionPercent) {
								return subIsDesc ? bCompletionPercent - aCompletionPercent : aCompletionPercent - bCompletionPercent;
							}

							// If completion percentages are equal, sort by date
							 if (!subIsDesc) {
							 	return a[subIndex] < b[subIndex] ? -1 : 1;
							 } else {
							 	return b[subIndex] < a[subIndex] ? -1 : 1;
							 }
						} else if (subIndex) {
						    if (!subIsDesc) {
							    return a[subIndex] < b[subIndex] ? -1 : 1;
						    } else {
							    return b[subIndex] < a[subIndex] ? -1 : 1;
						    }
					    }

					    if (sortResult !== 0) {
						    break;
					    }
				    }
			    }

			    return sortResult;
		    });

		    return items;
	    },
    	toggleFavorite(vaultId, $event) {
    		let self = this;
			let test = self.isFavorite(vaultId);
			console.log(test);
		    vaultService.mutation("setVaultAsFavorite", {'vault_id': vaultId, 'remove_vault': self.isFavorite(vaultId)}).then(response => {
		    	//console.log("Returned from set vault as favorite. " + response);
			    vaultService.query("getUserById", undefined, "user", {vuexCache: false}).then(() => {});
		    }).catch(err => {
		    	console.dir(err);
			    self.notification('error', "Unable to mark vault as a favorite.");
		    });
	    },
    	isFavorite(vaultId) {
    		return this.favoriteVaultSet.has(vaultId);
	    },
        momentDate(timestamp, dateOnly) {
            return moment.unix(timestamp / 1000).format(dateOnly ? "MM/DD/YYYY" : "MM/DD/YYYY hh:mm a");
        },
		yyyymmddTOmmddyyyy(timestamp, dateOnly) {
			let dateResult = null;
			if (timestamp) {
				let timeParts = timestamp.split("-");
				dateResult = timeParts[1] + "/" + timeParts[2] + "/" + timeParts[0];
			}
			return dateResult;
		},
        getVaultMemberInitials(vault) {
            let permissions = vault.permissions.filter(perm => perm._deleted !== 1);
            let initials = [];
			try{
				for (let p = 0; p < permissions.length; p++) {
					let username = permissions[p].user_name ? permissions[p].user_name : "";
					if(username){
						let hexColor = toMaterialStyle(username);
						initials.push({
							initials: getInitials(username),
							class: {
								"background-color": permissions[p].invitation_accepted === "true" ? hexColor.backgroundColor : "lightgray",
								"color": permissions[p].invitation_accepted === "true" ? hexColor.color : "black"
							},
							username: permissions[p].invitation_accepted === "true" ? username : username + " (Pending)"
						});
					}
				}
			}catch(e){
				console.log("Error getting initials for vault members.");
			}

            return initials;
        },
        isVaultPendingInvitation(vault) {
            let vaultIsPendingInvitation = false;
            if (this.user && this.user.vaults && this.cognito && this.cognito.attributes['custom:mutableUserType'] === "guest") {
            	let foundVault = this.user.vaults.find(testVault => testVault.vault_id === vault.id && testVault.invitation_accepted === "none" && testVault._deleted !== 1);
            	if (foundVault) {
		            vaultIsPendingInvitation = true;
	            }
            }
            return vaultIsPendingInvitation;
        },
        acceptInvite(vault, loadVault) {
        	//console.log("Accept Invitation");
        	let self = this;
            vaultService.mutation("vaultInvite", {vaultId: vault.id, acceptInvite: true}).then(() => {
	            self.notification('success', "Vault invite accepted.");
                if (loadVault === true) {
	                self.$router.push(`/org/${vault.org_id}/vault/${vault.id}/folder/main`);
                } else {
	                vaultService.query("getUserById", undefined, "user", {vuexCache: false}).then(() => {
		                self.fetchAllVaultPages().then(vaults => { self.vaults = vaults; });
	                });
                }
            }).catch(err => {
	            self.notification('error', "Unable to accept invite");
            });
        },
        denyInvite(vault) {
            vaultService.mutation("vaultInvite", {vaultId: vault.id, acceptInvite: false}).then(() => {
                vaultService.query("getUserById", undefined, "user", {vuexCache: false});
            }).catch(err => {
                this.notification('error', "Unable to dismiss invite");
            });
        },
        deleteVault(vault) {
            let self = this;
            vaultService.mutation("deleteVault", {vault_id: vault.id}).then(response => {
                self.notification('success', "Vault Deleted!");
                try {
					woopra.track("Deleted Vault", {Vault: vault.id, OrgId: self.organization.id ? self.organization.id : null });
				} catch (woopraError){
					console.error("Woopra not loaded.");
				}
                self.fetchAllVaultPages().then(vaults => { self.vaults = vaults; });
            }).catch(err => {
                this.notification('error', 'Error deleting selected vault.');
            });
        },
        archiveVault(vault) {
            let self = this;
            
            // Convert vault.archived to a proper boolean and determine new state
            let currentlyArchived = Boolean(vault.archived);
            let newArchivedState = !currentlyArchived;
            let actionText = newArchivedState ? 'archived' : 'unarchived';
            
            // The Lambda function requires ALL boolean fields to be passed as proper booleans
            let mutationParams = {
                "vault_id": String(vault.id),
                "org_id": String(vault.org_id),
                "archived": newArchivedState,
                "private_mode": Boolean(vault.private_mode) || false,
                "hide_perms": Boolean(vault.hide_perms) || false,
                "isTemplate": Boolean(vault.isTemplate) || false
            };
            
            vaultService.mutation("updateVaultSettings", mutationParams).then(function () {
                vault.archived = newArchivedState;
                self.notification('success', `Portal ${actionText} successfully.`);
            }).catch(function (error) {
                self.notification('error', `Unable to ${newArchivedState ? 'archive' : 'unarchive'} portal.`);
                console.error("updateVaultSettings Error:", error);
            });
        },
        createNewVault() {
            let self = this;
            if (self.newVaultName) {
                 vaultService.mutation("createVault", {name: self.newVaultName.trim(), org_id: self.organization.id}).then(result => {
					try {
						woopra.track("Portal Created", {Portal_Name : self.newVaultName, OrgId : self.organization.id});
					} catch (woopraError) {
						console.error("Woopra not loaded.");
					}
                    self.newVaultName = "";

                    self.notification('success', "Portal Created!");
                    self.fetchAllVaultPages().then(vaults => { self.vaults = vaults; });
                }).catch(err => {
                    self.notification('error', "Error creating new Client Portal.");
                });
            } else {
                self.notification('error', "Please enter a name for the new Client Portal.");
            }
        },
	    signOutUser() {
		    this.cache = {};
		    Auth.signOut({ global: true })
			    .then(data => {
				    EventBus.$emit('authState', {
					    status: 'signedOut'
				    });
			    })
			    .catch(err => console.log(err));
	    },
		togglePrivacyMode(value) {
			this.setLocalStoreValue("privateMode", value);
		},
		toggleArchivedPortals(value) {
			this.showArchivedPortals = value;
		},
		setLocalStoreValue(cname, cvalue) {
			localStorage.setItem(cname, cvalue);
		},
		getLocalStoreValue(cname) {
			let cvalue = null;
			if (localStorage[cname] === "true") {
				cvalue = true;
			} else if (localStorage[cname] === "false") {
				cvalue = false;
			} else {
				cvalue = localStorage[cname];
			}
			return cvalue;
		},
        saveTags(vault) {
            let self = this;
            let tags = Array.isArray(vault._editTagsArray) ? vault._editTagsArray.filter(Boolean) : [];
            vaultService.mutation('updateVaultSettings', {
                org_id: vault.org_id,
                vault_id: vault.id,
                tags: tags,
                archived: Boolean(vault.archived) || false,
                private_mode: Boolean(vault.private_mode) || false,
                hide_perms: Boolean(vault.hide_perms) || false,
                isTemplate: Boolean(vault.isTemplate) || false
            }).then(() => {
                self.snackBar = true;
                self.snackBarNotify = 'Tags updated!';
                self.snackBarType = 'success';
                self.$set(vault, 'tags', tags);
                self.$set(vault, '_editTagsArray', [...tags]);
            }).catch(() => {
                self.snackBar = true;
                self.snackBarNotify = 'Failed to update tags.';
                self.snackBarType = 'error';
            });
        },
		clearAllFilters() {
			this.searchVault = '';
			this.selectedWorkflowStates = [];
			this.filters = {
				name: '',
				tags: '',
				num_tasks_open: [0, 100],
				earliest_open_task_date: '',
				_lastChangedAt: '',
				workflowStateName: ''
			};
			this.vaultsTablePage = 1;
		},
		filterByWorkflowState(stateId) {
			// Toggle selection of workflow state
			if (this.selectedWorkflowStates.includes(stateId)) {
				// Remove if already selected
				const index = this.selectedWorkflowStates.indexOf(stateId);
				this.selectedWorkflowStates.splice(index, 1);
			} else {
				// Add to selection
				this.selectedWorkflowStates.push(stateId);
			}
			
			// Update the filter string for the table filtering
			this.updateWorkflowStateFilter();
			this.vaultsTablePage = 1;
		},
		clearWorkflowStateFilter() {
			// Clear both selected states and filter
			this.selectedWorkflowStates = [];
			this.filters.workflowStateName = '';
			this.vaultsTablePage = 1;
		},
		updateWorkflowStateFilter() {
			// Convert selected workflow states to filter string
			if (this.selectedWorkflowStates.length === 0) {
				this.filters.workflowStateName = '';
				return;
			}
			
			let filterNames = [];
			
			for (let stateId of this.selectedWorkflowStates) {
				if (stateId === 'not-set') {
					filterNames.push('not set');
				} else if (this.organization && this.organization.workflowStates) {
					const state = this.organization.workflowStates.find(s => s.id === stateId);
					if (state) {
						filterNames.push(state.name);
					}
				}
			}
			
			// Set filter as comma-separated string for multiple state filtering
			this.filters.workflowStateName = filterNames.join('|');
		},
		updateVaultWorkflowState(vault, newStateId) {
			let self = this;
			
			// Find the new state name
			let newStateName = null;
			if (newStateId && this.organization && this.organization.workflowStates) {
				const newState = this.organization.workflowStates.find(state => state.id === newStateId);
				newStateName = newState ? newState.name : null;
			}
			
			// Update the vault
			vaultService.mutation("updateVaultSettings", {
				org_id: vault.org_id,
				vault_id: vault.id,
				workflowStateId: newStateId,
				workflowStateName: newStateName,
				archived: Boolean(vault.archived) || false,
				private_mode: Boolean(vault.private_mode) || false,
				hide_perms: Boolean(vault.hide_perms) || false,
				isTemplate: Boolean(vault.isTemplate) || false,
				tags: vault.tags || []
			}).then(() => {
				// Update the local vault object
				self.$set(vault, 'workflowStateId', newStateId);
				self.$set(vault, 'workflowStateName', newStateName);
				
				self.snackBar = true;
				if (newStateName) {
					self.snackBarNotify = `Portal workflow state updated to: ${newStateName}`;
				} else {
					self.snackBarNotify = 'Portal workflow state cleared.';
				}
				self.snackBarType = 'success';
			}).catch((error) => {
				console.error("Error updating vault workflow state:", error);
				self.snackBar = true;
				self.snackBarNotify = 'Failed to update workflow state.';
				self.snackBarType = 'error';
			});
		}
    }
});

function getInitials(value) {
    let initials = [];
    try {
	    initials = value.toUpperCase().replace(/[^a-zA-Z- ]/g, "").match(/\b\w/g);
	    if (initials === null || initials.length === 0) {
		    initials = value.substring(0, 1).toUpperCase();
	    }
    } catch(e) {
    	console.error("Unable to get initials.");
    }

    return initials.join('');
}

function stringToColour(str) {
    var hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    var colour = '#';
    for (let i = 0; i < 3; i++) {
        let value = (hash >> (i * 8)) & 0xFF;
        colour += ('00' + value.toString(16)).substr(-2);
    }
    return colour;
}

function lightOrDark(color) {

    // Variables for red, green, blue values
    var r, g, b, hsp;

    // Check the format of the color, HEX or RGB?
    if (color.match(/^rgb/)) {

        // If RGB --> store the red, green, blue values in separate variables
        color = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);

        r = color[1];
        g = color[2];
        b = color[3];
    } else {

        // If hex --> Convert it to RGB: http://gist.github.com/983661
        color = +("0x" + color.slice(1).replace(
            color.length < 5 && /./g, '$&$&'));

        r = color >> 16;
        g = color >> 8 & 255;
        b = color & 255;
    }

    // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
    hsp = Math.sqrt(
        0.299 * (r * r) +
        0.587 * (g * g) +
        0.114 * (b * b)
    );

    // Using the HSP value, determine whether the color is light or dark
    if (hsp > 127.5) {

        return 'light';
    } else {

        return 'dark';
    }
}

const toBase64 = file => new Promise((resolve, reject) => {
	const reader = new FileReader();
	reader.readAsArrayBuffer(file);
	reader.onload = () => resolve(btoa(String.fromCharCode.apply(null, new Uint8Array(reader.result))));
	reader.onerror = error => reject(error);
});
